import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { resolveFranklyNetTarget } from '../../services/api';
import { resultsPath, docPath, viewPath } from './franklyNetHost';

const LOAD_TIMEOUT_MS = 9000;

const ORIGINS_ARTICLE = /^\/community\/article\/(\d+)\/([a-z0-9-]+)$/;

const CHROME_BUTTON = 'shrink-0 border border-gray-300 bg-white px-3 py-1 text-[12px] uppercase text-maroon hover:bg-gray-100';

function ChromeBar({ children }) {
    return (
        <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-black/20 bg-offwhite px-3 py-2">
            {children}
        </div>
    );
}

function Centred({ children }) {
    return (
        <div className="flex h-full items-center justify-center bg-white p-8 text-center">
            <div>{children}</div>
        </div>
    );
}

function FranklyNetViewer({ url, doc, query }) {
    const [target, setTarget] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(Boolean(url));
    const [frameFailed, setFrameFailed] = useState(false);
    const [frameLoaded, setFrameLoaded] = useState(false);
    const [docTitle, setDocTitle] = useState(null);
    const frameRef = useRef(null);
    const timerRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (doc) {
            setTarget(null);
            setError(null);
            setLoading(false);
            setFrameFailed(false);
            setFrameLoaded(false);
            setDocTitle(null);
            return undefined;
        }

        let active = true;
        setLoading(true);
        setError(null);
        setTarget(null);
        setFrameFailed(false);
        setFrameLoaded(false);

        resolveFranklyNetTarget(url)
            .then(res => {
                if (!active) return;
                if (res.success) setTarget(res);
                else setError(res.message || 'Frank cannot open that link.');
            })
            .catch(() => { if (active) setError('Frank cannot open that link.'); })
            .finally(() => { if (active) setLoading(false); });

        return () => { active = false; };
    }, [url, doc]);

    useEffect(() => {
        if (doc || !target || target.iframeAllowed === false) return undefined;

        timerRef.current = setTimeout(() => {
            setFrameFailed(current => (frameLoaded ? current : true));
        }, LOAD_TIMEOUT_MS);

        return () => clearTimeout(timerRef.current);
    }, [doc, target, frameLoaded]);

    const backToResults = () => {
        navigate(resultsPath(query));
    };

    const readDocTitle = () => {
        try {
            const title = frameRef.current?.contentDocument?.title || null;
            setDocTitle(title && title !== 'AskFrank' ? title.replace(/ - AskFrank$/, '') : null);
        } catch {
            setDocTitle(null);
        }
    };

    if (doc) {
        const origins = doc.match(ORIGINS_ARTICLE);

        return (
            <div className="flex h-full flex-col">
                <ChromeBar>
                    <button type="button" onClick={backToResults} className={CHROME_BUTTON}>
                        Back
                    </button>

                    <span className="min-w-0 flex-1 truncate text-[12px] text-gray-600">
                        {origins ? `origins.habbo.com${doc}` : (docTitle || 'AskFrank reader')}
                    </span>

                    {origins ? (
                        <>
                            <a
                                href={`${viewPath()}?doc=${encodeURIComponent(docPath('article', origins[2]))}${query ? `&q=${encodeURIComponent(query)}` : ''}`}
                                className={CHROME_BUTTON}
                                title="Frank keeps his own copy in case the live page will not load"
                            >
                                Frank&apos;s copy
                            </a>
                            <a
                                href={`https://origins.habbo.com${doc}`}
                                target="_blank"
                                rel="noreferrer noopener external"
                                className={CHROME_BUTTON}
                            >
                                Open in new tab
                            </a>
                        </>
                    ) : (
                        <span className="shrink-0 bg-maroon px-2 py-[1px] text-[10px] font-bold uppercase text-white">
                            Frank&apos;s copy
                        </span>
                    )}
                </ChromeBar>

                <iframe
                    key={doc}
                    ref={frameRef}
                    src={doc}
                    title={docTitle || 'AskFrank'}
                    onLoad={readDocTitle}
                    className="min-h-0 w-full flex-1 border-0 bg-white"
                />
            </div>
        );
    }

    if (loading) {
        return (
            <Centred>
                <p className="font-['Jersey_10'] text-[18px] uppercase text-gray-500">Opening that page...</p>
            </Centred>
        );
    }

    if (error || !target) {
        return (
            <Centred>
                <p className="font-['Jersey_10'] text-[20px] uppercase text-maroon">Frank cannot open that one</p>
                <p className="mt-2 text-[14px] text-gray-600">{error || 'That link went nowhere.'}</p>
                <button
                    type="button"
                    onClick={backToResults}
                    className="mt-4 border border-black/30 bg-maroon px-4 py-2 font-['Jersey_10'] text-[16px] uppercase text-white"
                    style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.4)' }}
                >
                    Back to results
                </button>
            </Centred>
        );
    }

    const cannotFrame = target.iframeAllowed === false || frameFailed;

    return (
        <div className="flex h-full flex-col">
            <ChromeBar>
                <button type="button" onClick={backToResults} className={CHROME_BUTTON}>
                    Back
                </button>

                <span className="min-w-0 flex-1 truncate text-[12px] text-gray-600" title={target.url}>
                    {target.title || target.url}
                </span>

                <a
                    href={target.url}
                    target="_blank"
                    rel="noreferrer noopener external"
                    className={CHROME_BUTTON}
                >
                    Open in new tab
                </a>
            </ChromeBar>

            {cannotFrame ? (
                <Centred>
                    <p className="font-['Jersey_10'] text-[20px] uppercase text-maroon">
                        This one will not sit in the frame
                    </p>
                    <p className="mx-auto mt-2 max-w-[520px] text-[14px] leading-relaxed text-gray-600">
                        {target.sourceName || 'That site'} asks browsers not to display it inside another page,
                        so Frank cannot show it here. Open it in a new tab instead.
                    </p>
                    <a
                        href={target.url}
                        target="_blank"
                        rel="noreferrer noopener external"
                        className="mt-4 inline-block border border-black/30 bg-maroon px-5 py-2 font-['Jersey_10'] text-[16px] uppercase text-white"
                        style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.4)' }}
                    >
                        Open {target.sourceDomain || 'the page'}
                    </a>
                </Centred>
            ) : (
                <iframe
                    key={target.url}
                    src={target.url}
                    title={target.title || 'AskFrank'}
                    onLoad={() => { setFrameLoaded(true); setFrameFailed(false); }}
                    referrerPolicy="no-referrer"
                    sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
                    className="min-h-0 w-full flex-1 border-0 bg-white"
                />
            )}
        </div>
    );
}

export default FranklyNetViewer;
